import React, { useMemo, useState } from 'react';
import { formatMontant, moisActifs } from '../utils/helpers';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';

const MOIS_ABBR = ['Jan','Fév','Mar','Avr','Mai','Jui','Jul','Aoû','Sep','Oct','Nov','Déc'];
const MOIS_FULL = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];


function journalBadge(action) {
  const a = (action || '').toLowerCase();
  if (a.includes('import'))                         return ['jb-import',    '📥', action];
  if (a.includes('adhes') || a.includes('creat'))   return ['jb-adhesion',  '➕', action];
  if (a.includes('point') || a.includes('paye') || a.includes('cotis')) return ['jb-pointage','💳', action];
  if (a.includes('valid') || a.includes('refus'))   return ['jb-validation','✅', action];
  if (a.includes('suppr') || a.includes('delet') || a.includes('radi')) return ['jb-suppression','🗑', action];
  if (a.includes('modif') || a.includes('edit'))    return ['jb-modif',     '✏️', action];
  if (a.includes('conseil') || a.includes('convoc'))return ['jb-conseil',   '⚖️', action];
  return ['jb-systeme', '⚙️', action];
}

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, padding:'8px 12px', fontSize:12, boxShadow:'0 4px 12px rgba(0,0,0,.1)' }}>
      <div style={{ fontWeight:700, marginBottom:4, color:'#374151' }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, display:'flex', gap:6, alignItems:'center' }}>
          <span style={{ display:'inline-block', width:8, height:8, background:p.color, borderRadius:2 }} />
          {p.name} : <strong>{Number(p.value).toLocaleString('fr-FR')} €</strong>
        </div>
      ))}
    </div>
  );
};

export default function Dashboard({ adherents, etablissements, cotisations, selectedYear, solde, setSolde, loadDemo, setPage, goToAdherent, journal, pendingCount, currentRole, demandes, comptabilite }) {
  const [editSolde, setEditSolde] = useState(false);
  const [soldeInput, setSoldeInput] = useState('');

  const actifs = adherents.filter(a => a.statut === 'Actif');
  const yearCots     = cotisations[selectedYear]     || {};
  const prevYearCots = cotisations[selectedYear - 1] || {};
  const currentMonth = new Date().getMonth() + 1;

  const stats = useMemo(() => {
    let collected = 0, expected = 0;
    actifs.forEach(adh => {
      const months = moisActifs(adh, yearCots);
      months.forEach(m => {
        const c = (yearCots[adh.id] || {})[m];
        if (c?.status === 'paye') collected += (adh.montant || 0);
      });
      expected += (adh.montant || 0) * months.length;
    });
    return { collected, expected };
  }, [actifs, yearCots]);

  const chartData = useMemo(() => MOIS_ABBR.map((label, mi) => {
    const m = mi + 1;
    let cur = 0, prev = 0;
    actifs.forEach(adh => {
      const cc = (yearCots[adh.id]     || {})[m];
      const pc = (prevYearCots[adh.id] || {})[m];
      if (cc?.status === 'paye') cur  += (adh.montant || 0);
      if (pc?.status === 'paye') prev += (adh.montant || 0);
    });
    return { label, [String(selectedYear)]: Math.round(cur), [String(selectedYear - 1)]: Math.round(prev) };
  }), [actifs, yearCots, prevYearCots, selectedYear]);

  const alerts = useMemo(() => {
    const result = [];
    const seenIds = new Set();

    // ── 1. Alertes depuis cotisations (pointage manuel ou import validé) ──
    actifs.forEach(adh => {
      const adhCots = yearCots[adh.id] || {};
      let consecutive = 0;
      for (let m = 1; m <= currentMonth; m++) {
        const s = adhCots[m]?.status || 'non_pointe';
        if (s === 'defaut_provision' || s === 'autre_motif') consecutive++;
        else if (s === 'paye') consecutive = 0;
      }
      if (consecutive >= 1) {
        result.push({ adh, consecutive, source: 'cotisation' });
        seenIds.add(adh.id);
      }
    });

    // ── 2. Alertes depuis type B du relevé BFC (même sans adherentId) ──
    const yearCompta = comptabilite?.[selectedYear] || {};
    for (let m = 1; m <= currentMonth; m++) {
      const ops = yearCompta[m]?.operations || [];
      ops.filter(o => o.type === 'B' && o.statut !== 'regularise').forEach(op => {
        if (op.adherentId && seenIds.has(op.adherentId)) return; // déjà compté
        if (op.adherentId) {
          const adh = actifs.find(a => a.id === op.adherentId);
          if (adh) {
            result.push({ adh, consecutive: 1, source: 'releve', mois: m, montant: op.debit, libelle: op.libelleBrut });
            seenIds.add(op.adherentId);
            return;
          }
        }
        // Impayé sans correspondance adhérent
        const key = op.libelleBrut || op.id;
        if (!seenIds.has(key)) {
          result.push({ adh: null, consecutive: 1, source: 'releve', mois: m, montant: op.debit, libelle: op.libelleBrut });
          seenIds.add(key);
        }
      });
    }

    return result.sort((a, b) => b.consecutive - a.consecutive);
  }, [actifs, yearCots, currentMonth, comptabilite, selectedYear]);

  const monthSummary = useMemo(() => {
    let paid = 0, unpaid = 0, pending = 0;
    actifs.forEach(adh => {
      const s = (yearCots[adh.id] || {})[currentMonth]?.status || 'non_pointe';
      if (s === 'paye') paid++;
      else if (s === 'defaut_provision' || s === 'arret_adhesion' || s === 'autre_motif') unpaid++;
      else pending++;
    });
    return { paid, unpaid, pending };
  }, [actifs, yearCots, currentMonth]);

  const taux = stats.expected > 0 ? Math.round(stats.collected / stats.expected * 100) : 0;
  const tauxColor = taux >= 70 ? '#15803d' : taux >= 40 ? '#d97706' : '#b91c1c';

  const allPending = (demandes || []).filter(d => d.statut === 'en_attente');
  const newAdh  = allPending.filter(d => !d.type || d.type === 'adhesion');
  const arrets  = allPending.filter(d => d.type === 'arret_adhesion');
  const ribs    = allPending.filter(d => d.type === 'changement_rib');

  return (
    <div>
      {/* ── En-tête ── */}
      <div className="page-header">
        <div>
          <div className="page-title">Tableau de bord</div>
          <div className="page-subtitle">SDASSP FO 974 — Exercice {selectedYear}</div>
        </div>
        <div className="flex gap-2">
          {adherents.length === 0 && (
            <button className="btn btn-outline" onClick={loadDemo}>📂 Charger démo</button>
          )}
          <button className="btn btn-outline" onClick={() => setPage('pointage')}>📅 Pointage</button>
        </div>
      </div>

      {/* ── Empty state ── */}
      {adherents.length === 0 && (
        <div className="alert alert-info mb-4">
          <span>ℹ️</span>
          <div>
            <strong>Aucune donnée chargée.</strong> Utilisez <em>Paramètres</em> pour importer votre base ou créez votre premier adhérent dans <em>Adhérents</em>.
          </div>
        </div>
      )}

      {/* ── Demandes en attente ── */}
      {allPending.length > 0 && (
        <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:20 }}>
          {newAdh.length > 0 && (
            <div className="dash-alert dash-alert--warning" onClick={() => setPage('validation')}>
              <span className="dash-alert-icon">🔔</span>
              <div className="dash-alert-body">
                <strong>{newAdh.length} demande{newAdh.length > 1 ? 's' : ''} d'adhésion</strong> en attente de validation.
              </div>
              <span className="dash-alert-link">Traiter →</span>
            </div>
          )}
          {arrets.length > 0 && (
            <div className="dash-alert dash-alert--danger" onClick={() => setPage('validation')}>
              <span className="dash-alert-icon">🛑</span>
              <div className="dash-alert-body">
                <strong>{arrets.length} demande{arrets.length > 1 ? 's' : ''} d'arrêt d'adhésion</strong> à traiter.
              </div>
              <span className="dash-alert-link">Traiter →</span>
            </div>
          )}
          {ribs.length > 0 && (
            <div className="dash-alert dash-alert--info" onClick={() => setPage('validation')}>
              <span className="dash-alert-icon">🏦</span>
              <div className="dash-alert-body">
                <strong>{ribs.length} changement{ribs.length > 1 ? 's' : ''} de RIB</strong> en attente.
              </div>
              <span className="dash-alert-link">Traiter →</span>
            </div>
          )}
        </div>
      )}

      {/* ── KPI cards ── */}
      <div className="grid-4 mb-4">
        {/* Adhérents */}
        <div className="stat-card stat-card--green">
          <div className="stat-label">Adhérents actifs</div>
          <div className="stat-value" style={{ color:'#15803d' }}>{actifs.length}</div>
          <div className="stat-sub">
            sur {adherents.length} · {adherents.filter(a => a.statut === 'Suspendu').length} susp. · {etablissements.length} structures
          </div>
        </div>

        {/* Collecté */}
        <div className="stat-card stat-card--red" style={{ borderTopColor:'#CC0000' }}>
          <div className="stat-label">Collecté {selectedYear}</div>
          <div className="stat-value">{formatMontant(stats.collected)}</div>
          <div className="stat-sub">sur {formatMontant(stats.expected)} attendus</div>
          <div className="progress mt-2">
            <div className="progress-bar" style={{ width:`${taux}%`, background: tauxColor }} />
          </div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:6 }}>
            <span className="text-xs text-gray">Taux de recouvrement</span>
            <span className="kpi-trend" style={{ background: taux>=70?'#dcfce7':taux>=40?'#fef3c7':'#fef2f2', color: tauxColor }}>
              {taux}%
            </span>
          </div>
        </div>

        {/* Solde bancaire */}
        <div className="stat-card stat-card--blue">
          <div className="stat-label">Solde bancaire</div>
          {editSolde ? (
            <div className="flex gap-2 items-center mt-1">
              <input className="form-control" type="number" step="0.01" value={soldeInput}
                onChange={e => setSoldeInput(e.target.value)} style={{ width:120 }} autoFocus />
              <button className="btn btn-primary btn-sm"
                onClick={() => { setSolde(parseFloat(soldeInput) || 0); setEditSolde(false); }}>✓</button>
              <button className="btn btn-outline btn-sm" onClick={() => setEditSolde(false)}>✕</button>
            </div>
          ) : (
            <>
              <div className="stat-value" style={{ cursor:'pointer', color: solde < 0 ? '#b91c1c' : '#1a1a1a' }}
                onClick={() => { setSoldeInput(String(solde)); setEditSolde(true); }}>
                {formatMontant(solde)}
              </div>
              <div className="stat-sub">Compte 00801428500 · cliquer pour modifier</div>
            </>
          )}
        </div>

        {/* Mois en cours */}
        <div className="stat-card stat-card--orange">
          <div className="stat-label">Mois en cours — {MOIS_FULL[currentMonth - 1]}</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:6, marginTop:8 }}>
            <div className="month-stat month-stat--green">
              <div className="month-stat-val">{monthSummary.paid}</div>
              <div className="month-stat-lbl">payés</div>
            </div>
            <div className="month-stat month-stat--red">
              <div className="month-stat-val">{monthSummary.unpaid}</div>
              <div className="month-stat-lbl">impayés</div>
            </div>
            <div className="month-stat month-stat--gray">
              <div className="month-stat-val">{monthSummary.pending}</div>
              <div className="month-stat-lbl">en attente</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Graphe + Alertes/Journal ── */}
      <div className="grid-2 mb-4">
        {/* Évolution mensuelle — Recharts */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Évolution des encaissements</span>
            <div style={{ display:'flex', gap:12, alignItems:'center' }}>
              <span style={{ fontSize:11, display:'flex', alignItems:'center', gap:4, color:'#CC0000', fontWeight:600 }}>
                <span style={{ display:'inline-block', width:10, height:10, background:'#CC0000', borderRadius:2 }} />{selectedYear}
              </span>
              <span style={{ fontSize:11, display:'flex', alignItems:'center', gap:4, color:'#bbb', fontWeight:600 }}>
                <span style={{ display:'inline-block', width:10, height:10, background:'#d1d5db', borderRadius:2 }} />{selectedYear - 1}
              </span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top:4, right:4, left:0, bottom:0 }} barCategoryGap="28%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize:11, fill:'#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize:11, fill:'#9ca3af' }} axisLine={false} tickLine={false}
                tickFormatter={v => v >= 1000 ? `${Math.round(v/100)/10}k` : v} width={36} />
              <Tooltip content={<ChartTip />} cursor={{ fill:'rgba(0,0,0,.04)' }} />
              <Bar dataKey={String(selectedYear - 1)} name={String(selectedYear - 1)} fill="#e5e7eb" radius={[3,3,0,0]} maxBarSize={18} />
              <Bar dataKey={String(selectedYear)}     name={String(selectedYear)}     fill="#CC0000" radius={[3,3,0,0]} maxBarSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Alertes + Journal */}
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {/* Alertes impayés */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Alertes impayés</span>
              {alerts.length > 0 && (
                <span className="badge badge-impaye">{alerts.length}</span>
              )}
            </div>
            {alerts.length === 0 ? (
              <div className="alert-ok">
                <span style={{ fontSize:16 }}>✅</span>
                Aucune alerte — tous les adhérents sont à jour.
              </div>
            ) : (
              <div style={{ maxHeight:200, overflowY:'auto' }}>
                {alerts.map((alert, i) => {
                  const { adh, consecutive, source, mois, montant, libelle } = alert;
                  const key = adh ? adh.id : `anon-${i}`;
                  const isClickable = !!adh;
                  const moisCourt = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
                  return (
                    <div key={key} className="adh-item"
                      onClick={() => isClickable && goToAdherent(adh.id)}
                      style={{ padding:'7px 10px', marginBottom:4, cursor: isClickable ? 'pointer' : 'default', opacity: adh ? 1 : 0.85 }}>
                      <div className="adh-avatar" style={{ width:32, height:32, fontSize:11, flexShrink:0, background: adh ? undefined : '#f3f4f6', color: adh ? undefined : '#6b7280' }}>
                        {adh ? `${(adh.prenom?.[0]||'').toUpperCase()}${(adh.nom?.[0]||'').toUpperCase()}` : '?'}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div className="adh-name" style={{ fontSize:13 }}>
                          {adh ? `${adh.prenom} ${adh.nom}` : <span style={{ fontFamily:'monospace', fontSize:11, color:'#374151' }}>{(libelle||'').slice(0,35)}</span>}
                        </div>
                        <div className="adh-sub">
                          {source === 'releve'
                            ? `Relevé BFC · ${mois ? moisCourt[mois-1] : ''} · ${montant ? Number(montant).toFixed(2).replace('.',',') + ' €' : ''}`
                            : `${consecutive} mois consécutif${consecutive > 1 ? 's' : ''}`}
                          {!adh && <span style={{ marginLeft:6, fontSize:10, color:'#d97706', fontWeight:600 }}>⚠️ non identifié — associer dans l'import</span>}
                        </div>
                      </div>
                      <span className="badge badge-impaye">{consecutive} mois</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Journal récent */}
          <div className="card" style={{ flex:1 }}>
            <div className="card-header">
              <span className="card-title">Journal d'activité</span>
              <span style={{ fontSize:11, color:'#9ca3af' }}>{journal.length} entrées</span>
            </div>
            {journal.length === 0 ? (
              <div className="empty-state" style={{ padding:'20px 0' }}>
                <div className="empty-state-icon" style={{ fontSize:28 }}>📋</div>
                <div className="empty-state-text" style={{ fontSize:13 }}>Aucune activité enregistrée.</div>
              </div>
            ) : (
              <div style={{ maxHeight:190, overflowY:'auto' }}>
                {journal.slice(0, 25).map(e => {
                  const [cls, , label] = journalBadge(e.action);
                  return (
                    <div key={e.id} className="journal-item-v2">
                      <span className="ji-time">
                        {new Date(e.date).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })}
                      </span>
                      <div className="ji-body">
                        <div>
                          <span className={`journal-badge ${cls}`}>{label}</span>
                        </div>
                        <div className="ji-text">{e.detail}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Résumé par structure ── */}
      {etablissements.length > 0 && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Résumé par structure</span>
            <span style={{ fontSize:12, color:'#9ca3af' }}>{etablissements.length} structures</span>
          </div>
          {/* Header row */}
          <div className="struct-row" style={{ borderBottom:'2px solid #f0f0f0', paddingBottom:6, marginBottom:4 }}>
            <span style={{ fontSize:11, fontWeight:700, color:'#9ca3af', textTransform:'uppercase', letterSpacing:'.4px' }}>Établissement</span>
            <span style={{ fontSize:11, fontWeight:700, color:'#9ca3af', textTransform:'uppercase', letterSpacing:'.4px', textAlign:'center' }}>Adh.</span>
            <span className="struct-progress" style={{ fontSize:11, fontWeight:700, color:'#9ca3af', textTransform:'uppercase', letterSpacing:'.4px' }}>Progression</span>
            <span style={{ fontSize:11, fontWeight:700, color:'#9ca3af', textTransform:'uppercase', letterSpacing:'.4px', textAlign:'right' }}>Collecté</span>
          </div>
          {etablissements
            .map(etab => {
              const members = actifs.filter(a => a.etablissementId === etab.id);
              let etabCollected = 0;
              members.forEach(adh => {
                Object.values(yearCots[adh.id] || {}).forEach(c => {
                  if (c.status === 'paye') etabCollected += (adh.montant || 0);
                });
              });
              return { etab, members, etabCollected };
            })
            .sort((a, b) => b.etabCollected - a.etabCollected)
            .map(({ etab, members, etabCollected }) => {
              const pct = stats.collected > 0 ? Math.min(100, Math.round(etabCollected / stats.collected * 100)) : 0;
              const barColor = pct > 30 ? '#CC0000' : pct > 10 ? '#d97706' : '#e5e7eb';
              return (
                <div key={etab.id} className="struct-row">
                  <span className="struct-name" title={etab.nom}>{etab.nom}</span>
                  <span className="struct-count">{members.length}</span>
                  <div className="struct-progress" style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ flex:1, height:6, background:'#f3f4f6', borderRadius:3, overflow:'hidden' }}>
                      <div style={{ width:`${pct}%`, height:'100%', background:barColor, borderRadius:3, transition:'width .4s' }} />
                    </div>
                    <span style={{ fontSize:11, color:'#9ca3af', width:28, textAlign:'right', flexShrink:0 }}>{pct}%</span>
                  </div>
                  <span className="struct-amount">{formatMontant(etabCollected)}</span>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
